import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography
} from "@mui/material";
import AudienceStep from "./CampaignSteps/AudienceStep";
import {
    addAudience,
    addParameter,
    cloneAudience,
    deleteAudience,
    deleteParameter,
    parametersFromApiAudience,
    setParameterData,
    updateParameter
} from "./campaignFormModel";
import {prepareParametersForSave} from "../CommonSummary";
import {useSaveCampaignMutation} from "../../core/reducers/campaign";
import Audience from "../Publisher/Audience";
import {isEmpty} from "../Utils/CommonUtils";

function deepSort(value) {
    if (Array.isArray(value)) {
        return value.map(deepSort).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
    } else if (value && typeof value === 'object') {
        return Object.keys(value).sort().reduce((acc, key) => {
            acc[key] = deepSort(value[key]);
            return acc;
        }, {});
    }
    return value;
}

function hashAudiences(audiences) {
    try {
        return JSON.stringify(deepSort(audiences || []));
    } catch (e) {
        return "";
    }
}

/** Конвертация audience из ответа API в аудитории для AudienceStep (как при редактировании кампании). */
function convertAudiencesToParamSets(sourceAudiences) {
    return (sourceAudiences || []).map((aud, idx) => ({
        name: `Audience ${idx + 1}`,
        count: idx + 1,
        parameters: parametersFromApiAudience(aud)
    }));
}

export default function AudienceBulkUpdateDialog({open, onClose, environment, selectedIds, items, onSaved}) {
    const [saveCampaign] = useSaveCampaignMutation();

    const selectedItems = useMemo(() => {
        if (!Array.isArray(selectedIds) || !items || typeof items !== 'object') return [];
        // возвращаем только item, а не весь объект
        return selectedIds.map(id => items[id]?.item).filter(Boolean);
    }, [selectedIds, items]);

    const [checkedIds, setCheckedIds] = useState(() => new Set(selectedIds || []));
    const [step, setStep] = useState('REVIEW'); // REVIEW -> EDIT
    const [saving, setSaving] = useState(false);
    const [saveErrors, setSaveErrors] = useState([]);

    // Группируем по «хэшу» audiences, чтобы подсветить одинаковые
    const groups = useMemo(() => {
        const map = new Map();
        selectedItems.forEach(it => {
            const key = hashAudiences(it.audiences);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(it.id);
        });
        return map;
    }, [selectedItems]);

    const isSameForChecked = useMemo(() => {
        const hashes = new Set();
        selectedItems.forEach(it => {
            if (checkedIds.has(it.id)) {
                hashes.add(hashAudiences(it.audiences));
            }
        });
        return hashes.size === 1 && checkedIds.size > 0;
    }, [checkedIds, selectedItems]);

    const handleToggle = (id) => {
        const s = new Set(checkedIds);
        if (s.has(id)) s.delete(id); else s.add(id);
        setCheckedIds(s);
    };

    const mainHash = useMemo(() => {
        // «эталонная» группа — по первому отмеченному
        for (const it of selectedItems) {
            if (checkedIds.has(it.id)) {
                return hashAudiences(it.audiences);
            }
        }
        return null;
    }, [checkedIds, selectedItems]);

    // Данные для шага EDIT
    const initialCampaignForEdit = useMemo(() => {
        const first = selectedItems.find(it => checkedIds.has(it.id)) || selectedItems[0];
        if (!first) return null;
        return {
            audiences: convertAudiencesToParamSets(first.audiences),
            fickle: !!first.fickle,
            repeatable: !!first.repeatable
        };
    }, [selectedItems, checkedIds]);

    const [editCampaign, setEditCampaign] = useState(initialCampaignForEdit);
    useEffect(() => {
        setEditCampaign(initialCampaignForEdit);
    }, [initialCampaignForEdit]);

    const [errorFields, setErrorFields] = useState([]);
    useEffect(() => {
        setEditCampaign(initialCampaignForEdit);
        setErrorFields([]);
    }, [initialCampaignForEdit]);


    // Хэндлеры для AudienceStep — те же иммутабельные операции, что и в конструкторе кампании
    // (раньше состояние мутировалось, а у нового параметра не было оператора и сохранение падало)
    const update = (fn) => setEditCampaign(current => fn(current));
    const audienceHandlers = {
        handleAddNewAudience: () => update(addAudience),
        handleCloneAudience: (index) => update(current => cloneAudience(current, index)),
        handleDeleteAudience: (index) => update(current => deleteAudience(current, index)),
        handleAddNewParams: (audienceIndex) => update(current => addParameter(current, audienceIndex)),
        handleDeleteParams: (audienceIndex, paramIndex) => update(current => deleteParameter(current, audienceIndex, paramIndex)),
        handleParameterDataChange: (params, value, paramIndex, audienceIndex) =>
            update(current => setParameterData(current, params, value, audienceIndex, paramIndex)),
        handleParameterOperatorChange: (value, paramIndex, audienceIndex) =>
            update(current => updateParameter(current, audienceIndex, paramIndex, {operator: value})),
        handleParameterValueChange: (value, paramIndex, audienceIndex) =>
            update(current => updateParameter(current, audienceIndex, paramIndex, {paramValue: value})),
        handleManualInput: (e, paramIndex, audienceIndex) =>
            update(current => updateParameter(current, audienceIndex, paramIndex, {paramValue: e.target.value})),
        handleManualInputForOffset: (e, fieldName, paramIndex, audienceIndex) =>
            update(current => updateParameter(current, audienceIndex, paramIndex, {[fieldName]: e.target.value})),
        handleVersionNumericParameterChange: (field, value, paramIndex, audienceIndex) =>
            update(current => updateParameter(current, audienceIndex, paramIndex, {[field]: value})),
    };

    const handleNext = () => setStep('EDIT');
    const handleBackToReview = () => setStep('REVIEW');

    const handleSave = useCallback(async () => {
        setSaving(true);
        setSaveErrors([]);
        const errors = [];

        // Готовим audiences для сохранения
        const audiencesForSave = [];
        (editCampaign.audiences || []).forEach(a => {
            let audienceForSave = {};
            for (let parameterSet of a.parameters) {
                audienceForSave = prepareParametersForSave(audienceForSave, parameterSet);
            }
            if (Object.keys(audienceForSave).length > 0) {
                audiencesForSave.push(audienceForSave);
            } else {
                // если полностью пустая аудитория — всё равно пушнем пустой объект
                audiencesForSave.push({});
            }
        });

        // Сохраняем каждый отмеченный элемент
        const toUpdateItems = selectedItems.filter(it => checkedIds.has(it.id));
        for (const it of toUpdateItems) {
            const payload = {
                id: it.id,
                groupId: it.groupId,
                priority: it.priority,
                fickle: !!editCampaign.fickle,
                repeatable: !!it.repeatable,
                type: it.type, // маппинг как в UpdateCampaignForm
                bundleId: it.bundleId,
                tags: it.tags,
                timeZone: it.timeZone, // если есть
                distribution: {
                    type: it.distribution?.type,
                    durationHour: it.distribution?.durationHour ?? 0,
                    from: it.distribution?.from,
                    till: it.distribution?.till
                },
                audiences: audiencesForSave
            };

            if(!isEmpty(it.matchmakingType)) {
                payload['matchmakingType'] = it.matchmakingType
            }

            if(!isEmpty(it.previuosCampaignId)) {
                payload['previuosCampaignId'] = it.previuosCampaignId
            }

            if(it.tournamentId !== null && it.tournamentId !== undefined) {
                payload['tournamentId'] = it.tournamentId
            }

            const saveParams = {
                ev: environment,
                body: payload
            };

            // Аналогично handleSave из формы кампаний — последовательное сохранение
            try {
                const res = await saveCampaign(saveParams);
                if (res?.error) {
                    errors.push(`${it.id}: ${res.error?.data?.message || 'Save error'}`);
                }
            } catch (e) {
                errors.push(`${it.id}: ${e?.message || 'Save error'}`);
            }
        }

        setSaving(false);
        setSaveErrors(errors);

        // раньше проверялось устаревшее значение saveErrors и диалог закрывался даже при ошибках
        if (errors.length === 0) {
            onSaved && onSaved();
            onClose && onClose();
        }
    }, [editCampaign, selectedItems, checkedIds, environment, saveCampaign, onSaved, onClose]);

    const renderReview = () => {
        return (
            <>
                <DialogTitle>Bulk Update audience</DialogTitle>
                <DialogContent dividers>
                    <Typography sx={{mb: 1}}>
                        Selected: {selectedItems.length}. Uncheck any unnecessary elements. Rows with identical audiences are highlighted.
                    </Typography>
                    <List dense>
                        {selectedItems.map(it => {
                            const itemHash = hashAudiences(it.audiences);
                            const sameGroupSize = (groups.get(itemHash) || []).length;
                            const isHighlighted = sameGroupSize > 1;
                            const isChecked = checkedIds.has(it.id);
                            const isSameWithTarget = mainHash && itemHash === mainHash;

                            return (
                                <ListItem
                                    key={it.id}
                                    sx={{
                                        borderRadius: 1,
                                        mb: 0.5,
                                        bgcolor: isHighlighted ? (isSameWithTarget ? 'success.light' : 'warning.light') : 'transparent',
                                        alignItems: 'flex-start'
                                    }}
                                    secondaryAction={
                                        <Typography variant="caption" color="text.secondary">
                                            {sameGroupSize > 1 ? `group: ${sameGroupSize}` : ''}
                                        </Typography>
                                    }
                                >
                                    <ListItemIcon sx={{alignSelf: 'flex-start', mt: 0.5}}>
                                        <Checkbox
                                            edge="start"
                                            checked={isChecked}
                                            tabIndex={-1}
                                            disableRipple
                                            onChange={() => handleToggle(it.id)}
                                        />
                                    </ListItemIcon>
                                    <Box sx={{flex: 1}}>
                                        <ListItemText
                                            primary={<Typography variant="subtitle2">{it.id}</Typography>}
                                            secondary={
                                                <Box className="campaign-audience">
                                                    {(it.audiences || []).map((audience, idx) => (
                                                        <Audience key={idx} audience={audience}>Audience {idx + 1}<br/></Audience>
                                                    ))}
                                                </Box>
                                            }
                                        />
                                    </Box>
                                </ListItem>
                            );
                        })}
                    </List>

                    {!isSameForChecked && (
                        <Alert severity="info" sx={{mt: 1}}>
                            Only campaigns with the same <strong>Audience</strong> should be selected.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button variant="contained" disabled={!isSameForChecked} onClick={handleNext}>Next</Button>
                </DialogActions>
            </>
        );
    };

    const renderEdit = () => {
        if (!editCampaign) return null;
        return (
            <>
                <DialogTitle>Редактирование audience</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} >

                            <Divider sx={{mb: 5}} />
                            <AudienceStep
                                currentCampaign={editCampaign}
                                setCurrentCampaign={setEditCampaign}
                                errorAudienceIndexes={[]}
                                errorFields={errorFields}

                                isTemplate={false}
                                {...audienceHandlers}
                                handleChange={(e) => {
                                    const {name, checked} = e.target || {};
                                    if (name === 'fickle') {
                                        setEditCampaign({...editCampaign, fickle: !!checked});
                                    } else if (name === 'repeatable') {
                                        setEditCampaign({...editCampaign, repeatable: !!checked});
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                    {saveErrors.length > 0 && (
                        <Box sx={{mt: 2}}>
                            {saveErrors.map((err, idx) => (
                                <Alert key={idx} severity="error" sx={{mb: 0.5}}>{err}</Alert>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleBackToReview} disabled={saving}>Back</Button>
                    <Button onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>Save</Button>
                </DialogActions>
            </>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            {step === 'REVIEW' ? renderReview() : renderEdit()}
        </Dialog>
    );
}
